<template>
  <div class="flex flex-1 w-full h-full">
    <div
      class="flex flex-col flex-1 gap-[20px] overflow-y-auto scroll-hidden transition-all duration-300 ease-in-out pt-[40px]"
      :class="
        openDetails && currentMode === 'dashboard' ? 'basis-1/2 px-[10px]' : 'basis-full  px-[10px] md:px-[30px]'
      "
    >
      <DashboardTabs
        :tabs="filteredModeTabs"
        :currentTab="currentMode"
        @tab-change="onModeTabChange"
        type="mode"
        v-if="can('can-see-calendar') || can('view-my-tasks')"
      />
      <template v-if="currentMode === 'dashboard'">
        <div class="flex flex-col w-full flex-1 gap-[20px] h-full overflow-y-auto pb-[30px]">
          <DashboardCards
            :order_items_by_status_group="dashboardData?.order_items_by_status_group ?? []"
            :splitter="openDetails ? true : false"
            :totalOrders="dashboardData?.items.total ?? 0"
            v-if="dashboardData && !isSuperOrOrderAdmin() && permissionLoaded"
          />
          <div class="flex flex-wrap justify-between w-full gap-4">
            <DashboardTabs
              :tabs="dashboardTabs"
              :currentTab="current_filters?.items_for ?? null"
              @tab-change="(tab) => onTabChange(tab)"
              type="dashboard"
            />
            <div class="flex flex-wrap justify-end flex-1 gap-2 min-w-fit">
              <DashboardFilters
                @update-filter="(filter: string | null) => getQuickFilterData(filter)"
                :currentTab="currentTab"
                :current-mode="'dashboard'"
              />
              <AssignOrderAdminModal
                @onSave="fetchOnSave(true)"
                v-if="can('assign-order-administrator')"
              />
              <ExportPriceModal v-if="can('export-order-prices')" :on-export="exportOrderPrices" />
              <button
                v-if="can('manual-create-order')"
                class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                @click="createManualOrder = true"
              >
                <PlusCircleIcon class="size-5 text-[#00B087]" />
                Create Order
              </button>

              <button
                v-if="can('shipment-booking')"
                :disabled="isDisabled"
                class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                :class="isDisabled ? 'opacity-50 cursor-not-allowed' : ''"
                @click="getShippmentBookingCsv"
              >
                Shipment Bookings
              </button>
            </div>
          </div>
          <DataTable
            ref="datatable"
            :update-key="`${currentMode}_${tableData.map(row => row.id).join('|')}`"
            :data="tableData"
            :columns="columns"
            :pageSizeOptions="[10, 20, 50, 100]"
            row_id="id"
            :isAsync="true"
            :totalItems="totalItems"
            @fetch-data="(data: any) => fetchRecords(data)"
            :currentPaginationInfo="current_filters"
            :searchable="true"
            search-placeholder="Type to search"
            :isLoading="isPending"
            :isFetching="isFetching"
            :error="error"
            @cell-click="handleCellClick"
            :showPagination="true"
            :showActionsColumn="openDetails ? false : true"
            @row-right-click="openDashboardActions"
            :rowClass="(row) => (selectedRow?.id === row.id ? 'bg-gray-300' : '')"
            :classProps="tableData?.length ? 'min-h-[600px]' : ''"
          >
            <template #actions="{ row }">
              <DashboardActions
                :current-tab="currentTab"
                @onFetch="fetchOnSave"
                @selectRow="handleRowSelected"
                :order_item_list="row"
                :showAsMenu="false"
                :order="selectedOrderDetails"
              />
            </template>
            <template #loading>
              <div class="flex items-center justify-center p-8">
                <div class="w-8 h-8 border-b-2 border-gray-900 rounded-full animate-spin"></div>
                <span class="ml-2 text-gray-600">Loading orders...</span>
              </div>
            </template>
            <template #error>
              <div class="flex items-center justify-center p-8 text-red-600">
                <span>Error loading orders. Please try again.</span>
              </div>
            </template>
          </DataTable>
        </div>
      </template>

      <template v-else-if="currentMode === 'calendar'">
        <div
          class="shadow-lg rounded-[10px] overflow-hidden flex flex-col h-full gap-[20px] overflow-y-auto"
        >
          <div class="flex flex-wrap justify-between flex-1 gap-2 mx-2 my-2">
            <div class="flex items-center gap-2">
              <Select
                :options="orderOptions"
                v-model="dashboardStore.selectedOrder"
                label="order_no"
                track-by="id"
                :filterable="true"
                :clearable="true"
                @search="onOrderSearch"
                @clear="dashboardStore.jumpToOrder()"
                placeholder="Search by Order No."
                class="min-w-[300px]"
              />
              <button
                class="flex items-center gap-1 px-3 py-2 text-white rounded bg-primary hover:bg-primary-dark"
                @click="dashboardStore.jumpToOrder"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16l4-4-4-4m8 8V8"
                  />
                </svg>
                Jump
              </button>
            </div>

            <div class="flex gap-2">
              <DashboardFilters
                @update-filter="(filter: string | null) => onCalendarFilterChange(filter)"
                :currentTab="currentTab"
                :currentMode="currentMode"
              />
              <button
                class="flex gap-1 items-center justify-center text-sm px-2 py-2 h-max font-medium text-[#00B087] border-[1px] border-[#00B087] bg-white rounded-[10px] whitespace-nowrap"
                @click="dashboardStore.exportCalendarData"
              >
                <ArrowUpTrayIcon class="size-5 text-[#00B087]" />
                Export Data
              </button>
            </div>
          </div>

          <Calender
            :key="dashboardStore.calendarKey"
            :initial-events="dashboardStore.events"
            :initial-holidays="dashboardStore.holidays"
            :allow-add-events="true"
            :allow-edit-events="true"
            :allow-delete-events="true"
            :show-sidebar="true"
            :show-mini-calendar="false"
            :show-upcoming-events="true"
            :initial-view="dashboardStore.currentView"
            :max-events-per-day="5"
            @event-updated="dashboardStore.handleEventUpdated"
            @event-created="dashboardStore.handleEventCreated"
            @event-deleted="dashboardStore.handleEventDeleted"
            @holiday-added="dashboardStore.handleHolidayAdded"
            @holiday-updated="dashboardStore.handleHolidayUpdated"
            @holiday-removed="dashboardStore.handleHolidayRemoved"
            @refresh-requested="dashboardStore.fetchCalendarData"
            @view-changed="handleViewChanged"
            :order-no-search="dashboardStore.orderNoSearch"
            :event-type-filter="dashboardStore.eventTypeFilter"
            :event-status-filter="dashboardStore.eventStatusFilter"
            :selected-date="dashboardStore.selectedCalendarDate"
            @jump-to-order="dashboardStore.jumpToOrder"
          >
            <template #event-tile="{ event, view, deletable, deleteEvent }">
              <div class="relative">
                <button
                  v-if="deletable"
                  class="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  @click.stop="deleteEvent(event.id)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3 h-3 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>

                <!-- Custom event content -->
                <div class="flex items-center space-x-1">
                  <!-- Event type icon -->
                  <div class="flex-shrink-0">
                    <svg
                      v-if="event.type === 'order'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                      <path
                        fill-rule="evenodd"
                        d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <svg
                      v-else-if="event.type === 'meeting'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    <svg
                      v-else-if="event.type === 'reminder'"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                      />
                    </svg>
                  </div>

                  <!-- Event title and details -->
                  <div class="flex-1 min-w-0">
                    <p class="font-medium truncate">{{ event.title }}</p>
                    <div class="flex space-x-1 text-xs text-gray-500">
                      <span v-if="event.order_no">Order# {{ event.order_no }}</span>
                      <span v-if="event.status" class="capitalize">
                        <span
                          class="inline-block w-2 h-2 mr-1 rounded-full"
                          :class="getStatusColor(event.status)"
                        ></span>
                        {{ event.status }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </Calender>
        </div>
      </template>
      <template v-else-if="currentMode === 'my-tasks'">
        <MyTasks />
      </template>
    </div>
    <div ref="dashboardActionsRef" v-if="showDashboardActions" class="absolute z-50">
      <DashboardActions
        :current-tab="currentTab"
        @onFetch="fetchOnSave"
        @selectRow="handleRowSelected(selectedRow)"
        @openOrderPriceModal="openOrderPriceModal"
        :order_item_list="{
          ...currentRow,
          factory_products: selectedOrderDetails?.items[0]?.factory_products ?? [],
        }"
        :showAsMenu="true"
        :order="selectedOrderDetails"
        @closeMenu="handleCloseAction"
      />
    </div>
    <Transition name="slide-in">
      <div
        v-if="openDetails && !['calendar', 'my-tasks'].includes(currentMode)"
        class="basis-1/2 border-l border-grey-300"
      >
        <OrderDetail
          :initialTab="paneDefaultTab"
          :currentTab="currentTab"
          :selectedOrderDetails="selectedOrderDetails"
          :isSidepaneLoading="isSidepaneLoading"
          :selectedRow="currentRow"
          :filteredOrderDetailTabs="filteredOrderDetailTabs"
          :currentOrderItem="currentOrderItem"
          :currentOrderItemIndex="currentOrderItemIndex"
          :sidebarOpen="sidebarOpen"
          :orderAdmins="store.orderAdmins"
          @fetchOnSave="fetchOnSave"
          @handleOrderMoved="handleOrderMoved"
          @handleOrderUpdated="handleOrderUpdated"
          @openOrderPriceModal="openOrderPriceModal"
          @detailsTabChange="detailsTabChange"
          @onClose="onDetailsClose"
        />
      </div>
    </Transition>

    <Transition name="fade">
      <CreateManualOrderModal
        v-if="createManualOrder"
        @onSave="fetchOnSave(true)"
        @close="() => (createManualOrder = false)"
      />
    </Transition>
    <Transition name="fade">
      <FactoryAssignModal
        v-if="assignFactoryModal"
        :orderItem="selectedOrderItem"
        @onSave="fetchOnSave(true)"
        @close="factoryModalClose"
        @handleOrderUpdated="handleOrderUpdated"
      />
    </Transition>
    <Transition name="fade">
      <PriceBreakdownModal
        v-if="priceBreakdownModal"
        :order_item="{
          ...(selectedOrderDetails?.items?.[0]
            ? { ...currentRow, ...selectedOrderDetails.items[0] }
            : currentRow),
          factory_products:
            selectedOrderDetails?.items?.[0]?.factory_products ??
            (currentRow as any)?.factory_products ??
            [],
        }"
        :order="selectedOrderDetails"
        :appUrl="appURL"
        @close="handlePriceBreakDownModalCloseEvent"
        @openOrderPriceModal="openOrderPriceModal"
        @fetchOnSave="fetchOnSave"
        :key="JSON.stringify(selectedOrderDetails)"
      />
    </Transition>
    <Transition name="fade">
      <AddInternalNoteModal
        v-if="internalNoteModal && internalNoteRow"
        :order_item_id="internalNoteRow.id"
        :internal_comment="
          internalNoteRow.internal_comment ? JSON.parse(internalNoteRow.internal_comment) : null
        "
        @on-close="handleInternalNoteClose"
        @on-cancel="
          () => {
            internalNoteModal = false;
            internalNoteRow = null;
          }
        "
      />
    </Transition>
  </div>
</template>
<script setup lang="ts">
import DashboardActions from '@/components/Dashboard/DashboardActions.vue';
import DashboardCards from '@/components/Dashboard/DashboardCards.vue';
import DashboardFilters from '@/components/Dashboard/DashboardFilters.vue';
import DashboardTabs from '@/components/Dashboard/DashboardTabs.vue';
import AddInternalNoteModal from '@/components/Dashboard/Modals/AddInternalNoteModal.vue';
import AssignOrderAdminModal from '@/components/Dashboard/Modals/AssignOrderAdminModal.vue';
import CreateManualOrderModal from '@/components/Dashboard/Modals/CreateManualOrderModal.vue';
import ExportPriceModal from '@/components/Dashboard/Modals/ExportPriceModal.vue';
import FactoryAssignModal from '@/components/Dashboard/Modals/FactoryAssignModal.vue';
import PriceBreakdownModal from '@/components/Dashboard/Modals/PriceBreakdownModal.vue';
import MyTasks from '@/components/Dashboard/MyTasks/MyTasks.vue';
import OrderDetail from '@/components/Dashboard/Order/OrderDetail.vue';
import Calender from '@/components/Shared/Calender.vue';
import DataTable from '@/components/Shared/DataTable.vue';
import Select from '@/components/Shared/Select.vue';
import { can, isSuperOrOrderAdmin } from '@/composables';
import { useDashboard } from '@/composables/useDashboard';
import { useMainStore } from '@/store';
import { ArrowUpTrayIcon, PlusCircleIcon } from '@heroicons/vue/24/outline';
import { onMounted } from 'vue';
const store = useMainStore();
const {
  appURL,
  dashboardTabs,
  permissionLoaded,
  isPending,
  error,
  isFetching,
  datatable,
  currentTab,
  createManualOrder,
  assignFactoryModal,
  selectedOrderDetails,
  isSidepaneLoading,
  priceBreakdownModal,
  filteredModeTabs,
  selectedOrderItem,
  internalNoteModal,
  internalNoteRow,
  currentRow,
  current_filters,
  filteredOrderDetailTabs,
  orderOptions,
  tableData,
  totalItems,
  columns,
  currentOrderItem,
  currentOrderItemIndex,
  currentMode,
  dashboardStore,
  dashboardData,
  sidebarOpen,
  paneDefaultTab,
  isDisabled,
  getStatusColor,
  handlePriceBreakDownModalCloseEvent,
  onOrderSearch,
  onModeTabChange,
  handleViewChanged,
  onCalendarFilterChange,
  factoryModalClose,
  fetchRecords,
  onTabChange,
  fetchOnSave,
  getQuickFilterData,
  handleCellClick,
  handleOrderMoved,
  handleOrderUpdated,
  handleInternalNoteClose,
  openOrderPriceModal,
  onDetailsClose,
  detailsTabChange,
  handleRowSelected,
  openDashboardActions,
  showDashboardActions,
  dashboardActionsRef,
  handleCloseAction,
  openDetails,
  selectedRow,
  exportOrderPrices,
  getShippmentBookingCsv,
} = useDashboard();

onMounted(async () => {
  await store.getFactories();
  await store.getOrderAdmins(['orderAdministrator', 'leadOrderAdministrator']);
  await store.getUnassignedOrders();
  await store.getCSManagers();
  await store.getSalesRepresentatives();
});

</script>
<style lang="scss">
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 10030;
  width: 100vw;
  background-color: #00000095;
  display: flex;
  justify-content: center;
  align-items: center;
}
.tooltip-container {
  .tooltip {
    opacity: 0;
    z-index: -1;
    transition: all 0.3s ease;
    transform: translateX(-30px) translateY(-3px);
    border-right: 4px solid #00b087;
    color: black;
    box-shadow: 0px 0px 10px #999999;
    padding: 5px 4px;
    white-space: nowrap;
    border-radius: 4px;
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: -7px;
      border-width: 5px;
      border-style: solid;
      transform: translateY(-50%);
      border-color: transparent white transparent transparent;
    }
  }
  &:hover {
    .tooltip {
      opacity: 1;
      transform: translateX(40px) translateY(-3px);
      z-index: 1;
    }
  }
}

/* Slide in/out from right */
.slide-in-enter-active,
.slide-in-leave-active {
  transition:
    transform 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
}
.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
.slide-in-enter-to,
.slide-in-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.custom-dqc-checkbox {
  appearance: none;
  width: 1rem;
  height: 1rem;
  border: 2px solid #bbbbbb; /* gray-300 */
  border-radius: 0.25rem; /* slightly rounded square */
  background-color: #fff;
  cursor: pointer;
  position: relative;
  transition: all 0.15s ease;
}

/* Hover effect */
.custom-dqc-checkbox:hover {
  border-color: #22c55e; /* green-500 */
}

/* Checked state */
.custom-dqc-checkbox:checked {
  background-color: #22c55e; /* green-500 */
  border-color: #22c55e; /* green border */
}

/* Tick mark */
.custom-dqc-checkbox:checked::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -55%);
  color: #fff;
  font-size: 0.7rem;
  font-weight: bold;
}

/* Disabled */
.custom-dqc-checkbox:disabled {
  cursor: not-allowed;
}
/* Optional: nice Tailwind-like shadow and rounding */
.absolute.z-50 {
  @apply bg-white rounded-lg shadow-lg;
}
</style>
